import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Bot, History, Minus, Plus, RotateCcw, UserRound } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, EmptyState, MarkdownBody, Skeleton, ToggleGroup, ToggleGroupItem, Tooltip, cn, diffWords, useSave } from "@ncp/ui";
import { SourceIcon, VersionTag, verdictMeta, voiceMeta } from "./ui";
import type { FindingChangeKind, ReviewCriterion, ReviewFinding, ReviewFindingVersion, ReviewSource } from "./api";
import { useStaffName } from "../../../lib/staff-names";

const kindBadge: Record<FindingChangeKind, "success" | "info" | "outline" | "gold"> = { Recorded: "success", Updated: "info", Cleared: "outline", Restored: "gold" };

/**
 * The full history of one criterion's finding: every version as an immutable record — who wrote it, when, what changed — with the
 * selected version read against its predecessor or against the current finding, field by field, and the option to make any earlier
 * version current again (which is itself written as a new version, so nothing is ever lost).
 */
export function VersionHistoryDialog({ caseId, criterion, open, onOpenChange, readOnly, onOpenSource }: { caseId: string; criterion: ReviewCriterion; open: boolean; onOpenChange: (o: boolean) => void; readOnly: boolean; onOpenSource: (s: ReviewSource) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const staffName = useStaffName();
  const api = useApi();
  const qc = useQueryClient();
  const ar = locale === "ar";
  const versions = useQuery({ queryKey: ["finding-versions", caseId, criterion.key], queryFn: () => api.get<ReviewFindingVersion[]>(`/api/cases/${caseId}/review/findings/${encodeURIComponent(criterion.key)}/versions`), enabled: open });
  const list = versions.data ?? [];
  const current = list[0];
  const [selectedNo, setSelectedNo] = useState<number | null>(null);
  const [compare, setCompare] = useState<"previous" | "current">("previous");
  useEffect(() => { if (open) { setSelectedNo(null); setCompare("previous"); } }, [open]);
  const selected = useMemo(() => list.find((v) => v.version === selectedNo) ?? current, [list, selectedNo, current]);
  const baseline = useMemo(() => {
    if (!selected) return undefined;
    if (compare === "current") return selected.version === current?.version ? list.find((v) => v.version === selected.version - 1) : current;
    return list.find((v) => v.version === selected.version - 1);
  }, [selected, compare, list, current]);

  const restore = useSave({
    mutationFn: (version: number) => api.post<ReviewFinding>(`/api/cases/${caseId}/review/findings/${encodeURIComponent(criterion.key)}/restore/${version}`, {}),
    expect: "record",
    saved: (f) => t("backoffice:review.versions.restored", { version: f.version }),
    onSuccess: () => { for (const k of [["case-review", caseId], ["finding-versions", caseId, criterion.key], ["case-events", caseId], ["case-audit", caseId]]) void qc.invalidateQueries({ queryKey: k }); setSelectedNo(null); },
  });

  const title = ar ? criterion.titleAr : criterion.title;
  const isCurrent = !!selected && selected.version === current?.version;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle><History className="me-2 inline size-4 text-aegold-700" />{t("backoffice:review.versions.title")}</DialogTitle>
          <DialogDescription>{title} · {t("backoffice:review.versions.count", { count: list.length })}{current ? ` · ${t("backoffice:review.versions.currentIs", { version: current.version })}` : ""}</DialogDescription>
        </DialogHeader>
        {versions.isLoading ? <Skeleton className="h-80" /> : list.length === 0 ? <EmptyState icon={History} title={t("backoffice:review.versions.empty")} className="py-10" /> : (
          <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
            <ol className="max-h-[60vh] space-y-1 overflow-y-auto pe-1 scrollbar-thin" aria-label={t("backoffice:review.versions.title")}>
              {list.map((v) => {
                const active = v.version === selected?.version;
                const Icon = verdictMeta[v.verdict].icon;
                return (
                  <li key={v.version}>
                    <button type="button" onClick={() => setSelectedNo(v.version)} aria-current={active ? "true" : undefined} className={cn("w-full rounded-xl border p-2.5 text-start transition-colors", active ? "border-aegold-400 bg-aegold-50/70" : "border-sand-200 bg-white hover:bg-sand-50")}>
                      <div className="flex items-center gap-2">
                        <VersionTag version={v.version} />
                        <Badge size="sm" variant={kindBadge[v.kind]}>{t(`backoffice:review.versions.kind.${v.kind}`)}</Badge>
                        {v.version === current?.version ? <Badge size="sm" variant="gold">{t("backoffice:review.versions.current")}</Badge> : null}
                        <Icon className={cn("ms-auto size-4 shrink-0", verdictMeta[v.verdict].tone)} />
                      </div>
                      <p className="mt-1.5 truncate text-xs font-medium">{staffName(v.changedByName)}{v.changedByRole ? <span className="font-normal text-muted-foreground"> · {t(`roles.${v.changedByRole}`, { defaultValue: v.changedByRole })}</span> : null}</p>
                      <p className="text-[11px] text-muted-foreground">{formatDateTime(v.changedAt, locale)}</p>
                      {v.changedFields.length ? <p className="mt-1 flex flex-wrap gap-1">{v.changedFields.map((f) => <span key={f} className="rounded bg-sand-100 px-1.5 py-0.5 text-[11px] uppercase tracking-wider text-sand-700">{t(`backoffice:review.versions.fields.${f}`)}</span>)}</p> : null}
                      {v.restoredFromVersion ? <p className="mt-1 text-[11px] text-aegold-800">{t("backoffice:review.versions.restoredFrom", { version: v.restoredFromVersion })}</p> : null}
                    </button>
                  </li>
                );
              })}
            </ol>

            {selected ? (
              <div className="min-w-0 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold">{t("backoffice:review.versions.versionN", { version: selected.version })}{isCurrent ? ` · ${t("backoffice:review.versions.current")}` : ""}</p>
                  {list.length > 1 ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{t("backoffice:review.versions.compareWith")}</span>
                      <ToggleGroup type="single" size="sm" value={compare} onValueChange={(v) => { if (v) setCompare(v as "previous" | "current"); }}><ToggleGroupItem value="previous" disabled={selected.version === 1}>{t("backoffice:review.versions.previous")}</ToggleGroupItem><ToggleGroupItem value="current" disabled={isCurrent}>{t("backoffice:review.versions.currentVersion")}</ToggleGroupItem></ToggleGroup>
                    </div>
                  ) : null}
                </div>

                <dl className="divide-y divide-sand-100 rounded-xl border border-sand-200 bg-white">
                  <Row label={t("backoffice:review.versions.fields.Verdict")} changed={!!baseline && baseline.verdict !== selected.verdict}>
                    <div className="flex flex-wrap items-center gap-2">
                      {baseline && baseline.verdict !== selected.verdict ? <><VerdictChip verdict={baseline.verdict} muted /><ArrowRight className="size-3.5 text-sand-400 rtl:rotate-180" /></> : null}
                      <VerdictChip verdict={selected.verdict} />
                    </div>
                  </Row>
                  <Row label={t("backoffice:review.versions.fields.Note")} changed={!!baseline && (baseline.note ?? "") !== (selected.note ?? "")}>
                    {baseline && (baseline.note ?? "") !== (selected.note ?? "") ? <NoteDiff before={baseline.note ?? ""} after={selected.note ?? ""} /> : selected.note ? <MarkdownBody className="prose-sm [&_p]:my-1">{selected.note}</MarkdownBody> : <p className="text-sm text-muted-foreground">—</p>}
                  </Row>
                  <Row label={t("backoffice:review.versions.fields.Evidence")} changed={!!baseline && evidenceKey(baseline.evidence) !== evidenceKey(selected.evidence)}>
                    <EvidenceDiff before={baseline?.evidence ?? (selected.version === 1 ? [] : selected.evidence)} after={selected.evidence} showDiff={!!baseline} onOpen={onOpenSource} />
                  </Row>
                  <Row label={t("backoffice:review.versions.aiAtTime")}>
                    {selected.aiVerdictAtReview ? (
                      <div className="flex items-start gap-2 text-sm">
                        <Bot className={cn("mt-0.5 size-4 shrink-0", voiceMeta.ai.icon)} />
                        <div className="min-w-0">
                          <p className="flex flex-wrap items-center gap-2"><VerdictChip verdict={selected.aiVerdictAtReview} />{selected.acceptedAiVerdict ? <Badge size="sm" variant="ai">{t("backoffice:r3Process.criterion.agreed")}</Badge> : null}</p>
                          {selected.aiSummaryAtReview ? <p className="mt-1 text-xs leading-relaxed text-sand-800" dir="auto">{selected.aiSummaryAtReview}</p> : null}
                        </div>
                      </div>
                    ) : <p className="text-sm text-muted-foreground">{t("backoffice:review.verdicts.Unknown")}</p>}
                  </Row>
                  <Row label={t("backoffice:review.versions.recordedBy")}>
                    <p className="flex flex-wrap items-center gap-1.5 text-sm"><UserRound className="size-3.5 text-sand-500" /> {staffName(selected.changedByName)}{selected.changedByRole ? <span className="text-muted-foreground">· {t(`roles.${selected.changedByRole}`, { defaultValue: selected.changedByRole })}</span> : null}<span className="text-muted-foreground">· {formatDateTime(selected.changedAt, locale)}</span><Badge size="sm" variant={kindBadge[selected.kind]}>{t(`backoffice:review.versions.kind.${selected.kind}`)}</Badge></p>
                  </Row>
                </dl>

                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-sand-100 pt-3">
                  <p className="text-[11px] text-muted-foreground">{t("backoffice:review.versions.immutableHint")}</p>
                  {!readOnly && !isCurrent ? (
                    <Tooltip content={t("backoffice:review.versions.restoreHint", { version: selected.version })}>
                      <Button variant="gold" size="sm" loading={restore.isPending} onClick={() => restore.mutate(selected.version)}><RotateCcw /> {t("backoffice:review.versions.restore", { version: selected.version })}</Button>
                    </Tooltip>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, changed, children }: { label: string; changed?: boolean; children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-1 px-3 py-2.5 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-3">
      <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-sand-600">{label}{changed ? <span className="rounded bg-warning-100 px-1 py-px text-[11px] font-medium normal-case tracking-normal text-warning-800">{t("backoffice:review.versions.changed")}</span> : null}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

function VerdictChip({ verdict, muted }: { verdict: string; muted?: boolean }) {
  const { t } = useTranslation();
  const meta = verdictMeta[verdict as keyof typeof verdictMeta] ?? verdictMeta.Pending;
  return <Badge size="sm" variant={muted ? "outline" : meta.badge} dot={!muted} className={cn(muted && "line-through")}>{t(`backoffice:review.verdicts.${verdict}`, { defaultValue: verdict })}</Badge>;
}

function NoteDiff({ before, after }: { before: string; after: string }) {
  const segments = useMemo(() => diffWords(before, after), [before, after]);
  return (
    <p className="whitespace-pre-wrap text-sm leading-relaxed" dir="auto">
      {segments.map((s, i) => s.kind === "same" ? <span key={i}>{s.text}</span> : s.kind === "added" ? <mark key={i} className="rounded bg-success-100 text-success-900 no-underline">{s.text}</mark> : <del key={i} className="rounded bg-danger-100 text-danger-800">{s.text}</del>)}
    </p>
  );
}

const evidenceKey = (list: ReviewSource[]) => list.map((e) => `${e.kind}:${e.id}`).sort().join("|");

function EvidenceDiff({ before, after, showDiff, onOpen }: { before: ReviewSource[]; after: ReviewSource[]; showDiff: boolean; onOpen: (s: ReviewSource) => void }) {
  const { t } = useTranslation();
  const ar = useLocale().locale === "ar";
  const beforeKeys = new Set(before.map((e) => `${e.kind}:${e.id}`));
  const afterKeys = new Set(after.map((e) => `${e.kind}:${e.id}`));
  const rows: Array<{ source: ReviewSource; state: "same" | "added" | "removed" }> = [
    ...after.map((e) => ({ source: e, state: (showDiff && !beforeKeys.has(`${e.kind}:${e.id}`) ? "added" : "same") as "same" | "added" })),
    ...(showDiff ? before.filter((e) => !afterKeys.has(`${e.kind}:${e.id}`)).map((e) => ({ source: e, state: "removed" as const })) : []),
  ];
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{t("backoffice:review.versions.noEvidence")}</p>;
  return (
    <ul className="space-y-1">
      {rows.map(({ source, state }) => (
        <li key={`${source.kind}:${source.id}:${state}`} className={cn("flex items-center gap-2 rounded-lg px-2 py-1 text-xs", state === "added" && "bg-success-50 text-success-900", state === "removed" && "bg-danger-50 text-danger-800 line-through")}>
          {state === "added" ? <Plus className="size-3 shrink-0" /> : state === "removed" ? <Minus className="size-3 shrink-0" /> : <SourceIcon kind={source.kind} />}
          <button type="button" className="min-w-0 truncate text-start hover:underline" onClick={() => onOpen(source)}>{(ar && source.titleAr) || source.title}</button>
          {source.locator ? <span className="shrink-0 text-sand-500">· {(ar && source.locatorAr) || source.locator}</span> : null}
          <span className="ms-auto shrink-0 rounded-sm bg-white/70 px-1 text-[11px] text-sand-600">{t(`backoffice:review.sourceKinds.${source.kind}`, { defaultValue: source.kind })}</span>
        </li>
      ))}
    </ul>
  );
}
